// COMPARE: everything the AC comparison tool says and decides. NO imports:
// scripts/prerender.js loads this file by stripping import/export, so the
// live page and the pre-rendered HTML run the very same code.
//
// Every function takes a `ctx`:
//   ctx.SERIES      SERIES from data/highlights.js (full-cooling and highest
//                   outside temperature, heating)
//   ctx.SPECS       SPECS from data/specs.js (the maker's spec sheet)
//   ctx.brands      the data/brands.js module (brandOf, typeOf, warrantyFor ...)
//   ctx.calcEnergy  calcEnergy from pages/ac-engine.js
//   ctx.KW_PER_TON  3.517, from pages/ac-engine.js
//   ctx.deliveryBadge  DELIVERY.badge from data/pricing.js
//   ctx.kitCopper   { rate, metres }: RATES.copperSplit.price (ex GST) and
//                   KIT_COPPER_METRES from data/pricing.js
//   ctx.clearanceWarranty  WARRANTY_TEXT from data/clearance.js
//
// A "variant" is one unit we sell at one price (buildVariants below): a
// catalogue model with or without the pipe kit, or a clearance unit. Its `id`
// is the token the tray and the URL carry: the model id for the kit sold by
// default, "<model id>~kit" / "<model id>~nokit" for the other one, and the
// clearance id for old stock. Prices and stock come only from PRICES / STOCK.
//
// LANGUAGE (Saheb, Oct 2026): every line is for an 8th-standard reader, and a
// difference is stated as a difference ("0.28 kW (0.08 TR) more cooling"),
// never as a remark about the size printed on the box.

export const COMPARE_SLUG = 'compare-ac';
export const COMPARE_TITLE = 'Compare ACs Side by Side: Price, Cooling, Features | Citi Cool';
export const COMPARE_META = 'Pick any ACs we sell and compare every spec side by side: price, exact cooling power in kW and TR, cooling in 46°C heat, filters, smart features, noise, size and warranty. One clear answer on which to buy.';
export const MAX_COMPARE = 4;

// Electricity. Same engine and the same cooling season as the AC calculator
// (Delhi: 8 months), at ₹8 a unit, so both pages show the same bill.
export const ELEC_RATE = 8;
export const COOLING_MONTHS = 8;
export const YEARS = 5;

// The hot afternoon every AC is checked at, and Saheb's derating rule: an AC
// loses about 5% of its cooling for every 1°C above its full-cooling
// temperature (the same rule the AC calculator uses).
export const HOT_C = 46;
export const DERATE_PER_C = 0.05;

// ─── the four quick questions ──────────────────────────────────────────────

export const QUESTIONS = [
  { key: 'floor', label: 'Which floor?', options: [
    { v: 'ground', label: 'Ground floor', short: 'Ground floor' },
    { v: 'middle', label: 'Middle floor', short: 'Middle floor' },
    { v: 'top', label: 'Top floor', short: 'Top floor' },
  ] },
  { key: 'hours', label: 'Hours of use a day?', options: [
    { v: 3, label: '2-4 hours', short: '2-4 hrs' },
    { v: 5, label: '4-6 hours', short: '4-6 hrs' },
    { v: 8, label: '6-10 hours', short: '6-10 hrs' },
    { v: 12, label: '10+ hours', short: '10+ hrs' },
  ] },
  { key: 'room', label: 'Room type?', options: [
    { v: 'bedroom', label: 'Bedroom', short: 'Bedroom' },
    { v: 'open', label: 'Living or open area', short: 'Living / open area' },
  ] },
  { key: 'heating', label: 'Heating in winter?', options: [
    { v: 'no', label: 'No', short: 'No heating' },
    { v: 'yes', label: 'Yes', short: 'Heating too' },
  ] },
];

export const DEFAULT_ANSWERS = { floor: 'middle', hours: 8, room: 'bedroom', heating: 'no' };

export const answersLine = (a) => QUESTIONS.map((q) => {
  const o = q.options.find((x) => String(x.v) === String(a[q.key]));
  return o ? o.short : '';
}).filter(Boolean).join(' · ');

// Pairs you want as their own page even when the two are not the same size
// or type. Model ids from catalogue.js. Edit freely.
export const POPULAR_COMPARISONS = [
  ['gtq-50', 'gtkc-50'],
  ['gtkc-50', 'gtkm-50'],
  ['gtkc-50', 'gtkl-50'],
  ['gtkm-50', 'ftku-50'],
  ['gtq-35', 'gtkc-35'],
  ['ftht-50', 'gtkc-50'],
  ['gtkm-50', 'jtkj-53'],
  ['gtkl-50', 'jtkj-53'],
];

export const COMPARE_FAQ = [
  {
    q: 'Is a 5 Star AC worth the extra money?',
    a: `Only if you run it for many hours. A 5 Star AC costs more to buy but uses less electricity. The saving on your bill is real, but it comes slowly. Look at the "Total cost in ${YEARS} years" row. If the 5 Star is still the costlier one there, it will not pay back its extra price within ${YEARS} years at your hours.`,
  },
  {
    q: 'Inverter or fixed speed: which should I buy?',
    a: 'An inverter AC slows down once the room is cool, so it uses less power over long hours. A fixed speed AC runs at full power, then switches off and on. It costs less to buy and much less to repair. For a few hours a day, a top floor or an open area, fixed speed is often the better buy. For a bedroom used 6 hours or more a night, an inverter usually wins.',
  },
  {
    q: 'What does "Cooling power 5.00 kW (1.42 TR)" mean?',
    a: 'It is the exact cooling the AC gives, from the maker’s own spec sheet. TR means ton of refrigeration: 1 TR is 3.517 kW. A higher number cools a room faster and copes better with heat.',
  },
  {
    q: `Why does the table show cooling "on a ${HOT_C}°C afternoon"?`,
    a: `Every AC gives its full cooling only up to a certain outside temperature. Above that, it loses about 5% of its cooling for every 1°C. Delhi afternoons reach ${HOT_C}°C, so this row shows how much cooling is left on such a day.`,
  },
  {
    q: 'With pipe kit or without: what is the difference?',
    a: 'It is the same AC. The "with kit" box also has a 3 m copper pipe kit. Take the kit for a new installation with no pipes in the wall. Take it without the kit when you replace an old AC and the old pipes can be reused.',
  },
  {
    q: 'Can a 1 ton AC cool a 1.5 ton room?',
    a: 'No. A bigger ton cools a bigger room. A 1 ton AC in a room that needs 1.5 ton will run all the time and still not cool it on a hot afternoon. Choose the size for your room first, then compare ACs of that size. Our AC calculator works out the size.',
  },
  {
    q: 'What does the electricity bill in the table mean?',
    a: `It is the electricity the AC uses in one year at the hours you picked, at ₹${ELEC_RATE} for each unit of electricity, over an ${COOLING_MONTHS}-month cooling season. It is worked out the same way as our AC calculator, from the AC’s cooling power and its efficiency number (ISEER). Your real bill will differ with your room, your settings and your electricity rate.`,
  },
  {
    q: 'Why does one AC say "On order"?',
    a: 'We hold some units in our own godown. Those get 24-hour delivery. The rest we order from the brand for you. That usually takes 5 to 7 days. The price shown is the price you pay.',
  },
];

// ─── helpers ───────────────────────────────────────────────────────────────

export const inr = (n) => `₹${Math.round(n).toLocaleString('en-IN')}`;
export const tonText = (soldAs) => `${Number(soldAs).toFixed(1)} Ton`;
export const two = (n) => Number(n).toFixed(2);
const K = (ctx) => (ctx && ctx.KW_PER_TON) || 3.517;
export const kwTr = (kw, ctx) => `${two(kw)} kW (${two(kw / K(ctx))} TR)`;

const listWords = (xs) => (xs.length < 2 ? xs.join('') : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`);

// Full-cooling temperature from the series. Never guessed.
export function fullCoolingAt(model, SERIES) {
  if (model.fullAt != null) return model.fullAt;
  const s = SERIES && SERIES[model.series];
  return s && s.heat && s.heat.full != null ? s.heat.full : null;
}

// The highest outside temperature it keeps running at.
export function maxOutsideAt(model, SERIES) {
  if (model.maxTemp != null) return model.maxTemp;
  const s = SERIES && SERIES[model.series];
  return s && s.heat && s.heat.max != null ? s.heat.max : null;
}

// Does it heat? true / false, or null when nobody has told us.
export function heatsInWinter(model, SERIES, spec) {
  if (typeof model.heats === 'boolean') return model.heats;
  if (spec && spec.heat) return true;
  const s = SERIES && SERIES[model.series];
  return s ? Boolean(s.heatingMin != null) : null;
}

// The indoor model code up to its capacity number: GTKC50XV16VKA -> GTKC50.
// Clearance units keep the code printed on their box.
export function shortCode(model, kit) {
  if (model.clearance && model.code) return model.code;
  const v = model.variants[kit] || model.variants.withoutKit || model.variants.withKit;
  const m = v && v.idu ? v.idu.match(/^[A-Z]+\d+/i) : null;
  return m ? m[0].toUpperCase() : model.id.toUpperCase();
}

const slugify = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

export function unitName(u) {
  return `${u.brand} ${shortCode(u.model, u.kit)}`;
}

const starsText = (u) => `${u.model.stars} Star${u.clearance ? '*' : ''}`;

// What kind of AC it is, in plain words: "3 Star inverter", "Hot & Cold, 3 Star".
export function kindText(u) {
  const m = u.model;
  const s = starsText(u);
  let kind;
  if (u.heats) kind = `Hot & Cold, ${s}`;
  else if (m.inverter === true) kind = `${s} inverter`;
  else if (m.inverter === false) kind = `${s}, fixed speed`;
  else kind = s;
  return u.type === 'window' ? `Window AC, ${kind}` : kind;
}

// Name plus kind, for sentences: "Daikin GTKC50 (3 Star inverter)".
export const unitLabel = (u) => `${unitName(u)} (${kindText(u)})`;

// ─── the units ─────────────────────────────────────────────────────────────

export function unitToken(model, kit, defaultKit) {
  if (kit === defaultKit) return model.id;
  return `${model.id}~${kit === 'withKit' ? 'kit' : 'nokit'}`;
}

// Every unit we sell at a price: each catalogue model with each kit that has
// a price, and every clearance unit still in stock. In stock first.
export function buildVariants({ CATALOGUE, PRICES, STOCK, onlineKit, CLEARANCE }) {
  const out = [];
  (CATALOGUE || []).forEach((m) => {
    const def = onlineKit(m);
    const kits = ['withoutKit', 'withKit'].filter((k) => m.variants[k] && typeof (PRICES[m.id] || {})[k] === 'number');
    const kitPrices = {};
    kits.forEach((k) => { kitPrices[k] = PRICES[m.id][k]; });
    const choices = kits.map((k) => ({ kit: k, id: unitToken(m, k, def) }));
    kits.forEach((k) => {
      const left = (STOCK[m.id] || {})[k];
      out.push({
        id: unitToken(m, k, def), model: m, kit: k, price: PRICES[m.id][k], mrp: m.variants[k].mrp || null,
        idu: m.variants[k].idu, inStock: (left || 0) > 0, soldOut: left === 0, left: left || 0,
        clearance: false, isDefault: k === def, kits: choices, kitPrices,
      });
    });
  });
  (CLEARANCE || []).forEach((u) => {
    const k = u.kit;
    const price = (PRICES[u.id] || {})[k];
    const left = (STOCK[u.id] || {})[k] || 0;
    if (typeof price !== 'number' || left <= 0) return;
    out.push({
      id: u.id, model: u, kit: k, price, mrp: null, idu: u.code, inStock: true, soldOut: false, left,
      clearance: true, isDefault: true, kits: [{ kit: k, id: u.id }], kitPrices: { [k]: price },
    });
  });
  const rank = (v) => (v.inStock ? 2 : v.soldOut ? 1 : 0);
  return out.sort((a, b) => rank(b) - rank(a));
}

// Units the tray, the size pages and the pair pages start from: the kit sold
// by default, Daikin catalogue only (clearance comes and goes with stock).
export const baseUnits = (units) => units.filter((u) => u.isDefault && !u.clearance);

export function yearlyBill(variant, answers, ctx) {
  const m = variant.model;
  if (!m.kw || !m.iseer || typeof m.inverter !== 'boolean') return null;
  return ctx.calcEnergy({
    ton: m.kw / K(ctx),
    iseer: m.iseer,
    type: m.inverter ? 'inverter' : 'non-inverter',
    dailyHrs: Number(answers.hours),
    coolingMonths: COOLING_MONTHS,
    elecRate: ELEC_RATE,
    targetTempC: 24,
    topFloor: answers.floor === 'top',
    highSun: false,
    poorInsul: false,
  }).yearly;
}

// A variant, with everything the table and the answer need.
export function toUnit(variant, answers, ctx) {
  const m = variant.model;
  const clearance = Boolean(variant.clearance || m.clearance);
  const spec = clearance ? null : ((ctx.SPECS || {})[m.id] || null);
  const yearly = yearlyBill(variant, answers, ctx);
  const fullAt = fullCoolingAt(m, ctx.SERIES);
  return {
    ...variant,
    id: variant.id || m.id,
    modelId: m.id,
    clearance,
    brand: ctx.brands.brandOf(m),
    type: ctx.brands.typeOf(m),
    spec,
    fullAt,
    maxAt: maxOutsideAt(m, ctx.SERIES),
    heats: heatsInWinter(m, ctx.SERIES, spec),
    kwAtHot: m.kw && fullAt != null ? m.kw * (1 - DERATE_PER_C * Math.max(0, HOT_C - fullAt)) : null,
    warranty: clearance ? null : ctx.brands.warrantyFor(m),
    warrantyNote: clearance ? (ctx.clearanceWarranty || null) : null,
    repair: clearance ? null : ctx.brands.repairFor(m),
    yearly,
    fiveYear: yearly == null ? null : variant.price + yearly * YEARS,
  };
}

export const toUnits = (variants, answers, ctx) => variants.map((v) => toUnit(v, answers, ctx));

// ─── slugs ─────────────────────────────────────────────────────────────────

// A size gets its own page only once we sell 2 or more ACs in it. Automatic:
// add a second 2.0 ton AC and /compare-2-0-ton-ac appears on the next build.
export function comparableSizes(models) {
  const count = {};
  models.forEach((m) => { count[m.soldAs] = (count[m.soldAs] || 0) + 1; });
  return Object.keys(count).filter((s) => count[s] >= 2).sort((a, b) => Number(a) - Number(b));
}

export const sizeSlug = (soldAs) => `compare-${String(Number(soldAs).toFixed(1)).replace('.', '-')}-ton-ac`;
export function sizeFromSlug(slug) {
  const m = String(slug).match(/^compare-(\d+)-(\d+)-ton-ac$/);
  return m ? `${m[1]}.${m[2]}` : null;
}
export const sizeTitle = (soldAs) => `${tonText(soldAs)} AC Comparison: Which One Should You Buy? | Citi Cool`;
export const sizeH1 = (soldAs) => `${tonText(soldAs)} AC comparison: which one should you buy?`;
export const sizeMeta = (soldAs, n) => `Every ${tonText(soldAs)} AC we sell, spec by spec: ${n} model${n === 1 ? '' : 's'} with today’s price, exact cooling power, cooling in ${HOT_C}°C heat, filters, noise, ${YEARS}-year cost and warranty. One clear answer on which to buy.`;

// Cheaper first, so B-vs-A is never a second page.
export function orderPair(a, b) {
  return a.price <= b.price ? [a, b] : [b, a];
}

export function pairSlug(a, b) {
  const [x, y] = orderPair(a, b);
  const part = (u) => `${slugify(u.brand)}-${slugify(shortCode(u.model, u.kit))}`;
  return `compare/${part(x)}-vs-${part(y)}`;
}

export function pairH1(a, b) {
  const [x, y] = orderPair(a, b);
  const yName = x.brand === y.brand ? shortCode(y.model, y.kit) : unitName(y);
  const same = x.model.soldAs === y.model.soldAs;
  return `${unitName(x)} vs ${yName}: which ${same ? `${tonText(x.model.soldAs)} ` : ''}AC is better?`;
}

export function pairTitle(a, b) {
  const [x, y] = orderPair(a, b);
  const yName = x.brand === y.brand ? shortCode(y.model, y.kit) : unitName(y);
  return `${unitName(x)} vs ${yName}: Price, Cooling and Features Compared | Citi Cool`;
}

export function pairMeta(a, b) {
  const [x, y] = orderPair(a, b);
  return `${unitName(x)} at ${inr(x.price)} vs ${unitName(y)} at ${inr(y.price)}. Exact cooling power, cooling in ${HOT_C}°C heat, filters, smart features, noise, ${YEARS}-year cost and warranty side by side, with one clear answer.`;
}

// Every pair worth a page: same size, same type, both priced; plus the
// POPULAR_COMPARISONS. Pass baseUnits(). Capped at `cap`, in-stock first.
export function allPairs(units, cap = 300) {
  const seen = new Set();
  const out = [];
  const push = (a, b) => {
    if (!a || !b || a.id === b.id) return;
    const slug = pairSlug(a, b);
    if (seen.has(slug)) return;
    seen.add(slug);
    out.push({ slug, a: orderPair(a, b)[0], b: orderPair(a, b)[1] });
  };
  const byId = new Map(units.map((u) => [u.id, u]));
  POPULAR_COMPARISONS.forEach(([x, y]) => push(byId.get(x), byId.get(y)));
  for (let i = 0; i < units.length; i += 1) {
    for (let j = i + 1; j < units.length; j += 1) {
      const a = units[i];
      const b = units[j];
      if (a.model.soldAs === b.model.soldAs && a.type === b.type) push(a, b);
    }
  }
  if (out.length <= cap) return out;
  const stocked = out.filter((p) => p.a.inStock || p.b.inStock);
  const rest = out.filter((p) => !(p.a.inStock || p.b.inStock));
  return [...stocked, ...rest].slice(0, cap);
}

export function findPair(units, slug) {
  const want = String(slug).replace(/^\/+|\/+$/g, '');
  const full = want.startsWith('compare/') ? want : `compare/${want}`;
  return allPairs(units, Infinity).find((p) => p.slug === full) || null;
}

// ─── default picks ─────────────────────────────────────────────────────────

const stockFirst = (a, b) => Number(b.inStock) - Number(a.inStock) || a.price - b.price;

// The kinds the AC calculator compares: fixed speed, then each star rating
// of inverter, up to three ACs. In stock first inside each kind.
export function defaultPicks(units, n = 3) {
  const kindOf = (u) => (u.model.inverter ? `${u.model.stars}star` : 'fixed');
  const kinds = [...new Set(units.map(kindOf))].sort((a, b) => {
    if (a === 'fixed') return -1;
    if (b === 'fixed') return 1;
    return parseInt(a, 10) - parseInt(b, 10);
  });
  const picks = [];
  kinds.forEach((k) => {
    const best = units.filter((u) => kindOf(u) === k && !u.soldOut).sort(stockFirst)[0]
      || units.filter((u) => kindOf(u) === k).sort(stockFirst)[0];
    if (best) picks.push(best);
  });
  const wanted = ['fixed', '3star', '5star'];
  const first = picks.filter((u) => wanted.includes(kindOf(u)));
  const others = picks.filter((u) => !wanted.includes(kindOf(u)));
  return [...first, ...others].slice(0, n);
}

// The size we sell the most models in; the size the tool opens with.
export function defaultSize(units) {
  const count = {};
  units.forEach((u) => { count[u.model.soldAs] = (count[u.model.soldAs] || 0) + 1; });
  return Object.keys(count).sort((a, b) => count[b] - count[a] || Number(b) - Number(a))[0] || null;
}

// ─── the quick answer ──────────────────────────────────────────────────────

const fiveOf = (u) => (u.fiveYear == null ? Infinity : u.fiveYear);
const byFiveYear = (a, b) => fiveOf(a) - fiveOf(b) || a.price - b.price;
const byFullAt = (a, b) => (b.fullAt || 0) - (a.fullAt || 0) || byFiveYear(a, b);

function choose(pool, order, reason) {
  if (!pool.length) return null;
  const sorted = [...pool].sort(order);
  const best = sorted[0];
  const bestInStock = sorted.find((u) => u.inStock) || null;
  return { unit: best, reason, bestInStock: bestInStock && bestInStock.id !== best.id ? bestInStock : null };
}

export function quickAnswer(units, answers) {
  if (!units.length) return null;
  const hours = Number(answers.hours);
  const cheapest = [...units].sort((a, b) => a.price - b.price)[0];
  const known5 = units.filter((u) => u.fiveYear != null);
  const cheapest5 = known5.length ? [...known5].sort(byFiveYear)[0] : null;

  let pool = units;
  let noHeat = false;
  if (answers.heating === 'yes') {
    pool = units.filter((u) => u.heats === true);
    if (!pool.length) noHeat = true;
  }

  let best = null;
  if (noHeat) {
    best = null;
  } else if (hours <= 4 || answers.floor === 'top' || answers.room === 'open') {
    const fixed = pool.filter((u) => u.model.inverter === false);
    if (fixed.length) {
      best = choose(fixed, byFiveYear, 'Cheaper to buy and much cheaper to repair. At a few hours a day, an inverter saves very little power.');
    } else if (answers.floor === 'top' && pool.some((u) => u.fullAt != null)) {
      const top = pool.filter((u) => u.fullAt != null);
      const max = Math.max(...top.map((u) => u.fullAt));
      best = choose(top, byFullAt, `Keeps full cooling up to ${max}°C outside. On a top floor that matters most.`);
    } else {
      best = choose(pool, byFiveYear, `Lowest total cost over ${YEARS} years.`);
    }
  } else if (answers.room === 'bedroom' && answers.floor !== 'top' && hours >= 6) {
    const inv = pool.filter((u) => u.model.inverter === true);
    best = inv.length
      ? choose(inv, byFiveYear, 'Used for long hours in a bedroom, so an inverter’s lower bill helps most.')
      : choose(pool, byFiveYear, `Lowest total cost over ${YEARS} years.`);
  } else {
    best = choose(pool, byFiveYear, `Lowest total cost over ${YEARS} years.`);
  }

  const warnings = [];
  if (new Set(units.map((u) => u.model.soldAs)).size > 1) {
    warnings.push({ key: 'size', text: 'These ACs are different sizes. A bigger size cools a bigger room. Choose the size for your room first.', link: '/ac-calculator', linkText: 'Find your size with the AC calculator' });
  }
  if (new Set(units.map((u) => u.type)).size > 1) {
    const lines = units.map((u) => `${unitName(u)}: ${u.type === 'window' ? 'window AC, one box that fits in a window.' : 'split AC, one part inside and one outside.'}`);
    warnings.push({ key: 'type', text: 'One is a split AC and one is a window AC. They fit in different ways.', lines });
  }

  return {
    cheapest,
    cheapest5,
    best,
    noHeat,
    noHeatText: noHeat ? 'None of these ACs can heat. All of them cool only. Pick an AC that heats, or turn heating off.' : null,
    warnings,
  };
}

// Plain-text lines of the answer, for the pre-rendered HTML and the tests.
export function answerLines(ans) {
  if (!ans) return [];
  const out = [`Cheapest to buy: ${unitLabel(ans.cheapest)} at ${inr(ans.cheapest.price)}.`];
  if (ans.cheapest5) out.push(`Cheapest over ${YEARS} years: ${unitLabel(ans.cheapest5)} at ${inr(ans.cheapest5.fiveYear)} (price + ${YEARS} years of electricity).`);
  if (ans.noHeat) out.push(`Best for your room: ${ans.noHeatText}`);
  else if (ans.best) {
    out.push(`Best for your room: ${unitLabel(ans.best.unit)}. ${ans.best.reason}`);
    if (!ans.best.unit.inStock) {
      out.push(ans.best.bestInStock
        ? `${unitName(ans.best.unit)} is not in stock right now. The best one in stock is ${unitName(ans.best.bestInStock)}.`
        : `${unitName(ans.best.unit)} is not in stock right now. We order it for you.`);
    }
  }
  return out;
}

// ─── key differences and "Choose X if" ─────────────────────────────────────

const feat = (u, key) => (u.spec && u.spec.f && u.spec.f[key] !== undefined ? u.spec.f[key] : null);
const hasFeat = (u, key) => feat(u, key) === true || (typeof feat(u, key) === 'string');

const FILTERS = [
  ['pm10', 'a PM1.0 filter'], ['pm25', 'a PM2.5 filter'], ['carbon', 'an active carbon filter'], ['streamer', 'Streamer'],
];
const SMART = [['wifi', 'a phone app'], ['voice', 'Alexa and Google voice control'], ['ieye', 'Intelligent Eye']];

function spread(units, get) {
  const have = units.filter((u) => get(u) != null);
  if (have.length < 2) return null;
  const sorted = [...have].sort((a, b) => get(b) - get(a));
  const hi = sorted[0];
  const lo = sorted[sorted.length - 1];
  if (get(hi) === get(lo)) return null;
  return { hi, lo, have };
}

export function keyDifferences(units, answers, ctx) {
  if (units.length < 2) return [];
  const out = [];
  const N = unitName;

  // Cooling power, to two decimals, as a difference.
  const p = spread(units, (u) => u.model.kw);
  if (p) {
    const d = p.hi.model.kw - p.lo.model.kw;
    out.push({ key: 'power', lead: 'Cooling power', text: p.have.length === 2
      ? `${N(p.hi)} gives ${two(d)} kW (${two(d / K(ctx))} TR) more cooling: ${kwTr(p.hi.model.kw, ctx)} against ${kwTr(p.lo.model.kw, ctx)} for ${N(p.lo)}.`
      : `${N(p.hi)} gives the most cooling: ${kwTr(p.hi.model.kw, ctx)}. ${N(p.lo)} gives the least: ${kwTr(p.lo.model.kw, ctx)}, ${two(d)} kW (${two(d / K(ctx))} TR) less.` });
  }

  // What is left on a hot afternoon.
  const h = spread(units, (u) => u.kwAtHot);
  if (h && h.hi.fullAt !== h.lo.fullAt) {
    const full = h.hi.fullAt >= HOT_C ? 'still gives its full' : 'gives';
    out.push({ key: 'hot', lead: `On a ${HOT_C}°C day`, text: `${N(h.hi)} ${full} ${kwTr(h.hi.kwAtHot, ctx)}. ${N(h.lo)} drops to ${kwTr(h.lo.kwAtHot, ctx)}, because its full cooling stops at ${h.lo.fullAt}°C.` });
  }

  // Smart features, grouped by who has them.
  const withSpec = units.filter((u) => u.spec);
  const smartOwners = SMART.filter(([k]) => new Set(withSpec.map((u) => hasFeat(u, k))).size > 1);
  if (smartOwners.length) {
    const owners = withSpec.filter((u) => smartOwners.some(([k]) => hasFeat(u, k)));
    const others = withSpec.filter((u) => !owners.includes(u));
    const names = smartOwners.filter(([k]) => owners.every((u) => hasFeat(u, k))).map(([, w]) => w);
    if (names.length) {
      out.push({ key: 'smart', lead: 'Smart features', text: `${listWords(owners.map(N))} ${owners.length > 1 ? 'have' : 'has'} ${listWords(names)}.${others.length ? ` ${listWords(others.map(N))} ${others.length > 1 ? 'have' : 'has'} none of these.` : ''}` });
    }
  }

  // Filters, per AC, only when they differ.
  const filterSets = withSpec.map((u) => FILTERS.filter(([k]) => hasFeat(u, k)).map(([, w]) => w));
  if (withSpec.length >= 2 && new Set(filterSets.map((s) => s.join('|'))).size > 1) {
    const text = withSpec.map((u, i) => (filterSets[i].length
      ? `${N(u)} has ${listWords(filterSets[i])}.`
      : `${N(u)} has no special filter.`)).join(' ');
    out.push({ key: 'air', lead: 'Clean air', text });
  }

  // Heating.
  const heaters = units.filter((u) => u.heats === true);
  if (heaters.length && heaters.length < units.length) {
    const lowC = heaters.map((u) => u.spec && u.spec.heat && u.spec.heat.minC).filter((x) => x != null);
    out.push({ key: 'heat', lead: 'Heating', text: `Only ${listWords(heaters.map(N))} ${heaters.length > 1 ? 'heat' : 'heats'} in winter${lowC.length ? `, down to ${Math.max(...lowC)}°C outside` : ''}. The others cool only.` });
  }

  // Noise: only a difference you can hear (3 dB or more).
  const q = spread(units, (u) => (u.spec ? u.spec.db : null));
  if (q && q.hi.spec.db - q.lo.spec.db >= 3) {
    out.push({ key: 'quiet', lead: 'Noise', text: `${N(q.lo)} is quieter at its lowest speed: ${q.lo.spec.db} dB against ${q.hi.spec.db} dB for ${N(q.hi)}.` });
  }

  // Stabiliser.
  const free = withSpec.filter((u) => u.spec.stabiliser === 'free');
  const inside = withSpec.filter((u) => u.spec.stabiliser === 'inside');
  if (free.length && inside.length) {
    out.push({ key: 'stab', lead: 'Voltage', text: `${listWords(inside.map(N))} ${inside.length > 1 ? 'have' : 'has'} a stabiliser built in. ${listWords(free.map(N))} ${free.length > 1 ? 'do' : 'does'} not, but ${free.length > 1 ? 'work' : 'works'} between ${free[0].spec.volts || 'its rated voltage'}.` });
  }

  // Electricity.
  const e = spread(units, (u) => u.yearly);
  if (e) {
    out.push({ key: 'bill', lead: 'Electricity', text: `${N(e.lo)} saves about ${inr(e.hi.yearly - e.lo.yearly)} a year against ${N(e.hi)}, at ${Number(answers.hours)} hours a day.` });
  }

  // Price, and what 5 years of bills do to it.
  const pr = spread(units, (u) => u.price);
  if (pr) {
    let text = `${N(pr.hi)} costs ${inr(pr.hi.price - pr.lo.price)} more than ${N(pr.lo)}.`;
    const known = units.filter((u) => u.fiveYear != null);
    if (known.length >= 2) {
      const l5 = [...known].sort(byFiveYear)[0];
      if (l5.id === pr.lo.id && pr.hi.fiveYear != null) {
        text += ` Even after ${YEARS} years of bills, ${N(pr.lo)} is ${inr(pr.hi.fiveYear - pr.lo.fiveYear)} cheaper.`;
      } else if (l5.id !== pr.lo.id && pr.lo.fiveYear != null) {
        text += ` But over ${YEARS} years, ${N(l5)} works out ${inr(pr.lo.fiveYear - l5.fiveYear)} cheaper than ${N(pr.lo)}, because of lower bills.`;
      }
    }
    out.push({ key: 'price', lead: 'Price', text });
  }

  // The same AC with and without the pipe kit.
  units.forEach((a) => {
    const b = units.find((x) => x !== a && x.modelId === a.modelId && x.kit !== a.kit);
    if (b && a.kit === 'withKit') {
      out.push({ key: `kit-${a.modelId}`, lead: 'Pipe kit', text: `${N(b)} and ${N(a)} are the same AC. ${N(a)} comes with a ${(ctx.kitCopper && ctx.kitCopper.metres) || 3} m copper pipe kit for ${inr(a.price - b.price)} more.` });
    }
  });

  // Delivery.
  const stocked = units.filter((u) => u.inStock);
  if (stocked.length && stocked.length < units.length) {
    out.push({ key: 'stock', lead: 'Delivery', text: `${listWords(stocked.map(N))} ${stocked.length > 1 ? 'are' : 'is'} in our godown: delivered within 24 hours. The others are ordered for you (5 to 7 days).` });
  }

  // Old stock.
  units.filter((u) => u.clearance).forEach((u) => {
    out.push({ key: `old-${u.id}`, lead: 'Old stock', text: `${N(u)} is a ${u.model.year} model in a sealed box, at a clearance price. Its stars follow the ${u.model.year} BEE norms.${u.left ? ` ${u.left} left.` : ''}` });
  });

  return out;
}

// "Choose X if ..." for each AC that wins at something.
export function chooseIf(units) {
  if (units.length < 2) return [];
  const uniqueBest = (get, dir) => {
    const have = units.filter((u) => get(u) != null);
    if (have.length < 2) return null;
    const vals = have.map(get);
    const target = dir === 'max' ? Math.max(...vals) : Math.min(...vals);
    const winners = have.filter((u) => get(u) === target);
    return winners.length === 1 && new Set(vals).size > 1 ? winners[0] : null;
  };
  const cheap = uniqueBest((u) => u.price, 'min');
  const cheap5 = uniqueBest((u) => u.fiveYear, 'min');
  const power = uniqueBest((u) => u.model.kw, 'max');
  const hot = uniqueBest((u) => u.fullAt, 'max');
  const quiet = (() => {
    const q = spread(units, (u) => (u.spec ? u.spec.db : null));
    return q && q.hi.spec.db - q.lo.spec.db >= 3 && units.filter((u) => u.spec && u.spec.db === q.lo.spec.db).length === 1 ? q.lo : null;
  })();
  const someLack = (key) => units.some((u) => u.spec && !hasFeat(u, key));

  return units.map((u) => {
    const pts = [];
    if (hot === u) pts.push('your room is on the top floor or gets strong afternoon sun');
    if (u.heats === true && units.some((x) => x.heats === false)) pts.push('you want heating in winter too');
    if (cheap === u) pts.push('you want the lowest price today');
    if (cheap5 === u) pts.push(`you want the lowest total cost over ${YEARS} years`);
    if (power === u && hot !== u) pts.push('you want the most cooling power');
    if ((hasFeat(u, 'wifi') || hasFeat(u, 'voice')) && (someLack('wifi') || someLack('voice'))) pts.push('you want to control it from your phone or by voice');
    if (hasFeat(u, 'pm25') && hasFeat(u, 'carbon') && someLack('carbon')) pts.push('you want filters for dust, smoke and cooking smells');
    if ((hasFeat(u, 'pm10') || hasFeat(u, 'streamer')) && (someLack('pm10') || someLack('streamer'))) pts.push('you want the finest filter and germ control');
    if (quiet === u) pts.push('it is for a bedroom and you want it quiet');
    if (u.model.inverter === false && units.some((x) => x.model.inverter === true)) pts.push('you use it only a few hours a day and want cheap repairs');
    if (u.model.inverter === true && units.some((x) => x.model.inverter === false)) pts.push('you run it many hours a day');
    if (u.inStock && units.some((x) => !x.inStock)) pts.push('you need it within 24 hours');
    const sibling = units.find((x) => x !== u && x.modelId === u.modelId && x.kit !== u.kit);
    if (sibling && u.kit === 'withKit') pts.push('it is a new installation with no pipes in the wall');
    if (sibling && u.kit === 'withoutKit') pts.push('you are replacing an old AC and the old pipes can be reused');
    return { id: u.id, title: `Choose ${unitName(u)} if`, points: pts.slice(0, 4) };
  }).filter((c) => c.points.length);
}

// "With pipe kit or without?" for the first AC on the table sold both ways.
export function kitBox(units, ctx) {
  const u = units.find((x) => x.kitPrices && typeof x.kitPrices.withKit === 'number' && typeof x.kitPrices.withoutKit === 'number');
  if (!u) return null;
  const wk = u.kitPrices.withKit;
  const wo = u.kitPrices.withoutKit;
  const metres = (ctx.kitCopper && ctx.kitCopper.metres) || 3;
  const rate = ctx.kitCopper && ctx.kitCopper.rate;
  const name = `${u.brand} ${shortCode(u.model, 'withoutKit')}`;
  const lines = [
    `It is the same AC. The "with kit" box also has a ${metres} m copper pipe kit.`,
    `${name} with the kit (sold as ${shortCode(u.model, 'withKit')}) costs ${inr(wk - wo)} more: ${inr(wk)} against ${inr(wo)}.`,
  ];
  if (rate) lines.push(`${metres} m of copper pipe from us costs ${inr(rate * metres)} + GST (${inr(rate)} a metre).`);
  return {
    title: 'With pipe kit or without?',
    lines,
    tips: [
      { lead: 'New installation, no pipes in the wall:', text: 'take the kit.' },
      { lead: 'Replacing an old AC and the pipes can be reused:', text: 'take it without the kit.' },
    ],
  };
}

// ─── the full spec table ───────────────────────────────────────────────────

const MISSING = 'Not given by brand';
const miss = () => ({ text: MISSING, missing: true });

// Cells for a numeric row. `better`: 'max', 'min' or null (no tick).
// `tickPool` limits who can win the tick (old stock's stars use older norms).
function numCells(units, get, fmt, better, tickPool) {
  const vals = units.map(get);
  const pool = (tickPool || units).map(get).filter((v) => typeof v === 'number');
  let best = null;
  if (better && pool.length >= 2 && new Set(pool).size > 1) best = better === 'max' ? Math.max(...pool) : Math.min(...pool);
  return vals.map((v, i) => (v == null ? miss() : {
    text: fmt(v, units[i]),
    tick: best != null && v === best && (!tickPool || tickPool.includes(units[i])),
  }));
}

function textCells(units, get) {
  return units.map((u) => {
    const v = get(u);
    return v == null ? miss() : { text: String(v) };
  });
}

function yesNoCells(units, get) {
  return units.map((u) => {
    const v = get(u);
    if (v == null) return miss();
    if (v === true) return { text: 'Yes', yes: true };
    if (v === false) return { text: 'No', no: true };
    return { text: String(v) };
  });
}

const dims = (d) => (d ? `${d[0]} x ${d[1]} x ${d[2]} mm` : null);

// The feature rows: key in SPECS.f, label, plain meaning.
const FEATURE_ROWS = {
  comfort: [
    ['air3d', '3D air flow', 'Swings the air up and down and left and right, so it reaches every corner.'],
    ['flaps', 'Dual flaps', 'Two flaps that push the air further into the room.'],
    ['chill', 'Fast cooling mode', 'A boost mode that cools the room faster when you walk in hot.'],
    ['econo', 'Econo mode', 'Caps how much power the AC draws, to keep the bill or the load down.'],
    ['dew', 'Dew Clean', 'Washes the indoor coil with its own water, for fresher air.'],
    ['triple', 'Triple display', 'A display on the indoor unit that shows the AC’s readings.'],
    ['backlit', 'Remote lights up', 'The remote’s buttons light up in the dark.'],
    ['selfDiag', 'Self diagnosis', 'Shows an error code when something goes wrong, so service is quicker.'],
    ['filterAlert', 'Filter cleaning alert', 'Tells you when the filter needs cleaning.'],
    ['displayOff', 'Display can be switched off', 'Turn off the indoor unit’s lights for a dark room at night.'],
    ['childLock', 'Child lock', 'Locks the remote so children cannot change the settings.'],
  ],
  air: [
    ['pm10', 'PM1.0 filter', 'Catches the finest dust and smoke particles.'],
    ['pm25', 'PM2.5 filter', 'Catches fine dust and smoke particles.'],
    ['carbon', 'Active carbon filter', 'Helps take cooking and other smells out of the room.'],
    ['streamer', 'Streamer', 'Daikin’s system that breaks down germs and allergens in the air passing through.'],
    ['prefilter', 'Anti-germ pre-filter', 'The first filter has a coating that stops germs growing on it.'],
    ['smell', 'Smell-proof', 'Stops musty smells building up inside the indoor unit.'],
  ],
  smart: [
    ['wifi', 'Phone app (WiFi)', 'Switch it on or change the setting from your phone, even before you reach home.'],
    ['voice', 'Voice control', 'Works with Amazon Alexa and Google Home.'],
    ['ieye', 'Intelligent Eye', 'Senses when the room is empty and eases off to save power.'],
  ],
};

// Sections of rows, in order. Each row: { key, label, means, cells, differs }.
// Empty rows (no AC has the fact) and empty sections are left out.
export function tableSections(units, ctx) {
  const k = K(ctx);
  const spec = (u, key) => (u.spec ? (u.spec[key] != null ? u.spec[key] : null) : null);
  const current = units.filter((u) => !u.clearance);

  const row = (key, label, means, cells) => {
    const known = cells.filter((c) => !c.missing).map((c) => c.text);
    return { key, label, means, cells, empty: known.length === 0, differs: new Set(known).size > 1 };
  };
  const featureRows = (list) => list.map(([key, label, means]) => row(key, label, means, yesNoCells(units, (u) => feat(u, key))));

  const sections = [
    { id: 'price', title: 'Price and running cost', rows: [
      row('price', 'Price today', 'What you pay us. GST included.', numCells(units, (u) => u.price, inr, 'min')),
      row('mrp', 'MRP', 'The maker’s printed price.', numCells(units, (u) => u.mrp, inr, null)),
      row('save', 'You save', 'MRP minus our price.', numCells(units, (u) => (u.mrp ? u.mrp - u.price : null), (v, u) => `${inr(v)} (${Math.round((v / u.mrp) * 100)}%)`, 'max')),
      row('yearly', 'Electricity bill in a year', `At the hours you picked, at ₹${ELEC_RATE} for each unit of electricity. Worked out the same way as our AC calculator.`, numCells(units, (u) => u.yearly, (v) => `${inr(v)} a year`, 'min')),
      row('fiveYear', `Total cost in ${YEARS} years`, `Price + ${YEARS} years of electricity bills.`, numCells(units, (u) => u.fiveYear, inr, 'min')),
      row('kit', 'Copper pipe kit in the box', 'A 3 m copper pipe kit. A new installation needs pipes; a replacement can often reuse the old ones.', yesNoCells(units, (u) => u.kit === 'withKit')),
      row('delivery', 'Delivery', '24-hour delivery only when the unit is in our godown. Otherwise we order it for you.', textCells(units, (u) => (u.inStock ? ctx.deliveryBadge : u.soldOut ? 'Sold out. We order it for you' : 'On order, 5 to 7 days'))),
    ] },
    { id: 'cooling', title: 'Cooling power', rows: [
      row('size', 'Size', 'The size class of the AC.', textCells(units, (u) => tonText(u.model.soldAs))),
      row('power', 'Cooling power', `The exact cooling the AC gives, from the maker’s spec sheet. 1 TR = ${k} kW.`, numCells(units, (u) => u.model.kw || null, (v) => kwTr(v, ctx), 'max')),
      row('range', 'Lowest to highest power', 'An inverter AC slows down or speeds up to suit the room. A higher top number cools a hot room faster. A fixed-speed AC always runs at full power.', (() => {
        const tops = units.map((u) => (u.model.inverter === true ? spec(u, 'maxKw') : null));
        const pool = tops.filter((v) => v != null);
        const top = pool.length >= 2 && new Set(pool).size > 1 ? Math.max(...pool) : null;
        return units.map((u, i) => {
          if (u.model.inverter === false) return { text: 'Always full power' };
          const lo = spec(u, 'minKw');
          const hi = spec(u, 'maxKw');
          if (lo == null || hi == null) return miss();
          return { text: `${two(lo)} to ${two(hi)} kW`, tick: top != null && tops[i] === top };
        });
      })()),
      row('fullAt', 'Full cooling up to', 'The hottest outside temperature at which it still gives its full cooling.', numCells(units, (u) => u.fullAt, (v) => `${v}°C outside`, 'max')),
      row('hot', `On a ${HOT_C}°C afternoon`, `Cooling drops about 5% for every 1°C above the full-cooling temperature. This is what is left on a ${HOT_C}°C day.`, numCells(units, (u) => u.kwAtHot, (v) => kwTr(v, ctx), 'max')),
      row('maxAt', 'Keeps running up to', 'Above this outside temperature the AC stops to protect itself.', numCells(units, (u) => u.maxAt, (v) => `${v}°C outside`, 'max')),
      row('cfm', 'Air flow', 'How much air it blows each minute. More air reaches the far corner sooner.', numCells(units, (u) => spec(u, 'cfm'), (v) => `${v} CFM`, 'max')),
    ] },
    { id: 'energy', title: 'Electricity use', rows: [
      row('stars', 'Star rating', 'More stars = lower electricity bill.', numCells(units, (u) => u.model.stars || null, (v, u) => starsText(u), 'max', current)),
      row('iseer', 'Efficiency number (ISEER)', 'How much cooling you get for each unit of electricity. Higher is better.', numCells(units, (u) => u.model.iseer || null, (v) => two(v), 'max')),
      row('bee', 'Units used in a year (star label)', 'The yearly electricity figure on the government star label. Lower is better.', numCells(units, (u) => (u.clearance ? null : u.model.annualKwh || null), (v) => `${two(v)} kWh`, 'min')),
      row('current', 'Current drawn', 'Lower current means less load on your house wiring and your inverter battery.', numCells(units, (u) => spec(u, 'currentA'), (v) => `${v} A`, 'min')),
    ] },
    { id: 'comfort', title: 'Comfort and noise', rows: [
      row('db', 'Quietest indoor sound', 'How quiet the indoor unit is at its lowest speed. Lower is quieter.', numCells(units, (u) => spec(u, 'db'), (v) => `${v} dB`, 'min')),
      row('speed', 'Speed', 'Inverter: slows down once the room is cool, so it uses less power. Fixed speed: runs at full power, then switches off and on.', textCells(units, (u) => (u.model.inverter === true ? 'Inverter' : u.model.inverter === false ? 'Fixed speed' : null))),
      ...featureRows(FEATURE_ROWS.comfort),
    ] },
    { id: 'air', title: 'Clean air', rows: featureRows(FEATURE_ROWS.air) },
    { id: 'smart', title: 'Smart features', rows: featureRows(FEATURE_ROWS.smart) },
    { id: 'heating', title: 'Heating', rows: [
      row('heats', 'Heats in winter', 'A Hot & Cold AC also warms the room in winter.', yesNoCells(units, (u) => u.heats)),
      row('heatDown', 'Heats down to', 'The coldest outside temperature at which it still heats.', textCells(units, (u) => {
        if (!units.some((x) => x.heats === true)) return null;
        if (u.spec && u.spec.heat) return `${u.spec.heat.minC}°C outside`;
        return u.heats === false ? 'Does not heat' : null;
      })),
    ] },
    { id: 'build', title: 'Build and installation', rows: [
      row('stabiliser', 'Stabiliser', 'Protects the AC when your voltage goes up or down.', textCells(units, (u) => {
        const s = spec(u, 'stabiliser');
        if (s === 'inside') return 'Built in';
        if (s === 'free') return `Not built in. Works between ${u.spec.volts}`;
        return null;
      })),
      row('swing', 'Swing compressor', 'Daikin’s own compressor design.', yesNoCells(units, (u) => feat(u, 'swing'))),
      row('coating', 'Anti-rust coating', 'A coating on the outdoor unit that protects it from rust and pollution.', yesNoCells(units, (u) => feat(u, 'coating'))),
      row('idu', 'Indoor unit size', 'Height x width x depth. Check your wall space.', textCells(units, (u) => dims(spec(u, 'idu')))),
      row('odu', 'Outdoor unit size', 'Height x width x depth. Check your balcony or wall space.', textCells(units, (u) => dims(spec(u, 'odu')))),
      row('pipe', 'Longest pipe run', 'How far apart the indoor and outdoor units can be, and how much higher one can be.', numCells(units, (u) => (spec(u, 'pipe') ? u.spec.pipe[0] : null), (v, u) => `${v} m long, ${u.spec.pipe[1]} m high`, 'max')),
    ] },
    { id: 'warranty', title: 'Warranty and service', rows: [
      row('warranty', 'Warranty', 'The compressor is the heart of the AC: it does the cooling. The control board (PCB) is its brain.', textCells(units, (u) => (u.warrantyNote || ctx.brands.warrantyText(u.warranty)))),
      row('repair', 'If it breaks after warranty', 'What a repair usually costs, from our own service work.', textCells(units, (u) => u.repair)),
      row('year', 'Model year', 'Old stock is a sealed, never-installed unit from an earlier year.', textCells(units, (u) => (u.clearance ? `${u.model.year}, old stock` : 'Current model'))),
    ] },
  ];

  return sections
    .map((s) => ({ ...s, rows: s.rows.filter((r) => !r.empty) }))
    .filter((s) => s.rows.length);
}

// Flat rows, for the pre-rendered HTML and the tests.
export function tableRows(units, ctx) {
  return tableSections(units, ctx).flatMap((s) => s.rows.map((r) => ({ ...r, section: s.title })));
}

export const STAR_FOOTNOTE = '*Star rating of old stock is the one on its own label, under the BEE norms of its manufacturing year. Current BEE norms are stricter, so the same unit may get fewer stars today.';

export function faqSchema(faqs) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };
}
